/**
 * @name Header
 * @description Encabezado con columnas del reporte y ordenamiento de columnas
 * @author Ricardo Guerra
 */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SortableContainer, SortableElement } from 'react-sortable-hoc';

// Material-UI
import MuiTableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import Slider from '../components/slider';

const Header = (props) => {
  const {
    columns,
    orderColumns,
    onReorderColumn,
    onResizeColumn
  } = props;

  const element = document.getElementById('report');
  if (element) {
    let ordColVis = [];
    // Orden de las columnas visibles
    columns.map((column, index) => {
      if (column.visible) {
        ordColVis[orderColumns.findIndex((i) => i === index)] = orderColumns.find((i) => i === index);
      }
    });

    // Filtrar solo columnas visibles
    ordColVis = ordColVis.filter(() => true);

    columns.map((column, index) => {
      if (column.visible) {
        if (element.scrollWidth > element.clientWidth) {
          // Si la tabla ('report') ya tiene scroll horizontal, entonces agregar width o minWidth a las columnas para evitar conflictos.
          column.width = column.width ? column.width : column.minWidth;
        } else {
          if (ordColVis[ordColVis.length - 1] === index) {
            // Si no tiene scroll y es el último elemento del orden, entonces eliminar width.
            column.width = '';
          } else {
            // Si no es el último elemento, entonces si tiene width mantener y si no eliminar.
            column.width = column.width === column.minWidth ? '' : column.width;
          }
        }
      }
    });
  }

  // Use the react-sortable-hoc wrappers around Material-UI elemnts.
  const SortableHead = SortableContainer(({ children }) => {
    return (
      <TableHead>
        <TableRow>
          {children}
        </TableRow>
      </TableHead>
    );
  });

  const SortableCell = SortableElement(({ value }) => {
    return <>{value}</>;
  });

  const shouldCancelStart = (e) => {
    // Cancel sorting if the event target is an [div] with ['||']
    if (e.target.tagName.toLowerCase() === 'div' && e.target.textContent === '||') {
      return true; // Return true to cancel sorting
    }
  };

  const resizeSlideHandler = (delta, id) => {
    const { columns } = props;
    const element = document.getElementById('report');
    const columnWidth = document.getElementById(`column-${id}`).offsetWidth;
    const newWidth = columnWidth + delta;

    columns.map((column, i) => {
      if (column.visible) {
        if (i === id) {
          if (newWidth < column.minWidth) {
            // Si el nuevo width de la columna es menor al minWidth de la columna, asignar un minWidth.
            column.width = `${column.minWidth}px`;
          } else {
            // Si no, asignar el nuevo width.
            column.width = `${newWidth}px`;
          }
        } else {
          if (element.scrollWidth > element.clientWidth) {
            // Si la tabla ('report') ya tiene scroll horizontal mantener width a las columnas para evitar conflictos.
            return;
          } else {
            if (columns.lenght - 1 === i)
              // NOTA: Si no, eliminar width del ultimo elemento del orden.
              column.width = '';
            else {
              // Si no es el ultimo elemento, mantener el width y si no eliminarlo.
              column.width = column.width ? column.width : '';
            }
          }
        }
      }
    });

    onResizeColumn(columns);
  };

  return (
    <SortableHead
      axis="x"
      shouldCancelStart={shouldCancelStart}
      onSortEnd={onReorderColumn}
    >
      {orderColumns.map((colIdx, index) => (
        // [index] needs to be CURRENT
        // [key] needs to be STATIC
        columns[colIdx].visible && (
          <SortableCell
            index={index}
            key={colIdx}
            value={
              <TableCell
                id={`column-${colIdx}`}
                key={colIdx}
                align={columns[colIdx].align}
                style={{
                  width: columns[colIdx].width || '',
                  minWidth: columns[colIdx].minWidth
                }}
              >
                <HeaderContainer>
                  <span id={`column-label-${colIdx}`}>
                    {columns[colIdx].label}
                  </span>
                  <span>
                    <Slider
                      index={colIdx}
                      onSlide={resizeSlideHandler}
                    />
                  </span>
                </HeaderContainer>
              </TableCell>
            }
          />
        )
      ))}
    </SortableHead>
  );
};

Header.propTypes = {
  // Colección de columnas a mostrar en el grid. 
  columns: PropTypes.array.isRequired,
  // Array con el orden de las columnas.
  orderColumns: PropTypes.array.isRequired,
  // Función a ejecutar cuando se desea ordenar la columna.
  onReorderColumn: PropTypes.func,
  // Función a ejecutar cuando se cambia el ancho de la columna.
  onResizeColumn: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  & > span {
    font-family: 'Bryant Pro Medium';
    font-size: ${(props) => props.theme.typography.bodyText};
    font-weight: normal;
    line-height: 1.4;
    letter-spacing: normal;
    vertical-align: middle;
    color: ${(props) => props.theme.colors.greyDark};
  }

`;

// TableCell
const TableCell = styled(MuiTableCell)`
  &.MuiTableCell-root {
    position: relative;
    padding: 12px;  
  }

  &.MuiTableCell-head {
    border: 1px solid ${(props) => props.theme.colors.greyLight} !important;
    cursor: move;
  }
`;

export default Header;
